import React from 'react';
import { ArrowRight, Quote, ExternalLink, Star } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import LatestFromBlog from '../../components/v2/LatestFromBlog';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';
import PortfolioGrid from '../../components/v2/PortfolioGrid';
import { HERO } from '../../data/heroImages';
import FeaturedClientBuild from '../../components/v2/FeaturedClientBuild';

// Real, on-file testimonials from Audio & Co. podcast & publishing clients.
// All quotes are verbatim from client-submitted reviews.
const TESTIMONIALS = [
  {
    quote: 'Absolutely the best!!! If you are looking for a podcast producer, look no further. Jo has been instrumental in getting The Scrumptious Woman off the ground in no time. Less than 1 month from first speaking it was launched. Within 2 months we are ranking in the top 10% worldwide.',
    name: 'Juliette Karaman',
    role: 'Host, The Scrumptious Woman podcast',
    service: 'Podcast launch & production',
  },
  {
    quote: 'Jo, you have been such a fantastic part of my publishing journey. Thanks for your amazing formatting skills, guidance and reassurance with my book. Thanks for your time and enthusiasm with my audiobook preparations too. You are so professional, thoughtful and inspiring.',
    name: 'Jennifer Jones',
    role: 'Poet & Author',
    service: 'Book publishing & audiobook',
  },
  {
    quote: 'I am so grateful to have entered Jo\u2019s world. Being introduced to Jo was one of those life-changing moments. I was instantly put at ease by her professional approach and able to complete my project quickly and effortlessly thanks to her skills. Working with Jo has been an absolute delight.',
    name: 'Vivienne Rawnsley',
    role: 'Hypnotherapist & Author',
    service: 'Publishing & production',
  },
  {
    quote: 'I could NEVER have gotten my book out into the world without Jo and her incredible team. I had so many questions along the way and never once did she make me feel anything but supported and understood. She made sure I LOVED how she\u2019d done things and went out of her way to give me loads of options. If you are considering working with her, DO IT.',
    name: 'Andee Martineau',
    role: 'Author, parenting & connection',
    service: 'Book publishing',
  },
  {
    quote: 'I couldn\u2019t have done it without them. Superb! I highly recommend Jo and the team who have been absolutely amazing from the start. I knew I wanted to launch a podcast, but I had no idea how to go about it and Jo has been there advising, supporting and encouraging me every step of the way. I now have a rolling planner and schedule that means I\u2019m in control and can focus on doing what I do best.',
    name: 'Lucy Rennie',
    role: 'Host, Future Proof your Business Podcast',
    service: 'Podcast launch & strategy',
  },
  {
    quote: 'Amazingly supportive, professional and helpful. Jo continued the amazing support throughout this whole process with her formatting work, and I will definitely be looking to record with her. I had no idea what to expect and she went through everything and gave me the time and support needed. The seamless process this has all been.',
    name: 'Sarah Bailey',
    role: 'Author & Publishing Client',
    service: 'Book formatting',
  },
  {
    quote: 'Professional, knowledgeable and efficient. Jo is fab. This is my second time working with her on the formatting of my book and she was so helpful at getting it done again.',
    name: 'Carol Deveney',
    role: 'Repeat Publishing Client',
    service: 'Book formatting',
  },
  {
    quote: 'I only had a connection call with Jo and OMG, this woman is something. So passionate and knowledgeable, she is just so magnetic. Jo gave me tons of information that I had no clue what is needed to start a podcast. It is rare when I find such people and I am so grateful.',
    name: 'Oksana Tsimpoaka',
    role: 'Aspiring Podcaster',
    service: 'Podcast strategy call',
  },
  {
    // VERBATIM quote from the printed acknowledgements in "Phonecalls: A
    // True Crime Memoir" (photo of the printed page supplied by Jo).
    quote: 'These books would never have been released if not for one person. She has always been my friend, Jo Day, now so proud to say my publisher. She read my manuscript, insisting on bringing the story to life. I can never thank her enough for believing in the story of Glyn and Deb. Jo, you will always be my Fairy Godmother. Thank you.',
    name: 'Deb Cullen',
    role: 'True-Crime Author \u00b7 Letters + Phonecalls',
    service: 'Copy edit, proof read, formatting, cover, KDP launch',
  },
];

const BEFORE_AFTER = [
  { label: 'Posts per week',            before: '0 to 3 (inconsistent)',              after: '37+ (every week)' },
  { label: 'Time you spend on content', before: '~10 hrs/week + decision fatigue',    after: '~45 mins recording + 15-min check-in' },
  { label: 'Where your content lives',  before: 'Scattered, deleted, unfindable',     after: 'Searchable library on your domain' },
  { label: 'YouTube channel rhythm',    before: 'Quiet for months',                   after: '1 long video + 21 Shorts + 7 Community posts weekly' },
];

const CASE_STUDIES = [
  {
    headline: 'The full Pro Plan at full scale: website, podcast, YouTube, 70+ articles with audio, chatbot, two programmes, book, audiobook + journal.',
    business: 'The Dimple Method™, Dimple Amore',
    body: 'Our most comprehensive Pro Plan build to date. Brand-new business website on her own domain, YouTube channel managed end-to-end, podcast production, 70+ long-form articles each with audio narration, a fully trained AI chatbot, two new flagship programmes launched (Dimple in Your Pocket and The Roadmap to Purpose training programme), her book formatted to publishing-house standards, her audiobook recorded and produced, and a companion journal designed and produced in-house. BBC-featured, best-selling author, top 5% global podcast.',
    siteImage: null,
    liveLink: { href: '/case-studies/dimple-method', label: 'Read The Dimple Method case study', internal: true },
  },
  {
    headline: 'Launched in under a month. Top 10% worldwide in two.',
    business: 'The Scrumptious Woman, Juliette Karaman',
    body: 'From first conversation to a live podcast in less than 30 days. Within 8 weeks the show was ranking in the top 10% of podcasts worldwide. We handled concept, production, distribution, and ongoing weekly episodes.',
  },
  {
    headline: 'A full business container in one place: website, chatbot, podcast, YouTube, articles and audiobook.',
    business: 'Thrive \u00b7 Treat Your Business, Katie Bell',
    body: 'Katie\u2019s whole coaching business on her own domain. Business website build, a fully trained AI chatbot, podcast strategy and production (211 episodes and counting), YouTube with 87 long-form videos and 2,413 Shorts, 237 SEO articles, 14 topic hubs linking every asset, and her audiobook recorded and produced. Trusted by 500+ UK clinic owners.',
    siteImage: { src: '/portfolio/thrive/home.jpg', alt: 'Thrive business coaching homepage for clinic owners, a website built for SEO by Audio & Co. for Katie Bell', href: 'https://thrive-businesscoaching.com' },
    liveLink: { href: '/case-studies/thrive-katie-bell', label: 'Read the Thrive case study', internal: true },
  },
  {
    headline: 'Book + audiobook delivered, ready for Amazon.',
    business: 'Jennifer Jones, Poet & Author',
    body: 'Manuscript formatted to publishing-house standards across paperback, hardcover and ebook. Audiobook prep, narration coaching, and full ACX / Audible distribution. From manuscript to multi-format launch without the technical headache.',
  },
  {
    headline: 'Professional services launch, frictionless.',
    business: 'Vivienne Rawnsley, Hypnotherapist',
    body: 'Vivienne came in mid-project and we got her assets shipped quickly and quietly. A reminder that the engine works just as well for one-off launches as it does for a full Pro Plan run.',
  },
  {
    headline: 'Two true-crime memoirs, on Amazon, done for her.',
    business: 'Deb Cullen, True-Crime Author',
    body: 'Letters and Phonecalls: A True Crime Memoir — end-to-end done-for-her. Copy edit, proof read, book formatting (paperback, hardcover, Kindle), cover design, Amazon KDP setup and launch. Both books live on Amazon.co.uk.',
    covers: [
      { src: 'https://m.media-amazon.com/images/I/61e2WMTGi8L._SY500_.jpg', alt: 'Letters: A True Crime Memoir by Deb Cullen, book cover designed and published on Amazon by Audio & Co.', href: 'https://www.amazon.co.uk/dp/B0FXY1RG59' },
      { src: 'https://m.media-amazon.com/images/I/61d02IRNZyL._SY500_.jpg', alt: 'Phonecalls: A True Crime Memoir by Deb Cullen, book cover designed and published on Amazon by Audio & Co.', href: 'https://www.amazon.co.uk/dp/B0HDJ93SS1' },
    ],
  },
];

// Schema.org Review markup for each testimonial (helps Google/AI surface real reviews).
const REVIEW_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  'name': 'Audio & Co.',
  'url': 'https://audioandco.com',
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '5',
    'bestRating': '5',
    'worstRating': '1',
    'reviewCount': String(TESTIMONIALS.length),
  },
  'review': TESTIMONIALS.map((t) => ({
    '@type': 'Review',
    'reviewRating': { '@type': 'Rating', 'ratingValue': '5', 'bestRating': '5' },
    'author': { '@type': 'Person', 'name': t.name },
    'reviewBody': t.quote,
    'itemReviewed': { '@type': 'Service', 'name': t.service },
  })),
};

export default function Results() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Podcast & Publishing Client Reviews | Audio & Co."
        description="Real reviews and results from Audio & Co. podcast, publishing and website clients, including top 10% podcasts and bestselling authors."
        keywords={['podcast production reviews', 'book publishing testimonials', 'audio and co reviews', 'jo day reviews', 'podcast agency case studies', 'audiobook production reviews']}
        url="https://audioandco.com/results"
      />

      {/* Inline schema.org Review data so Google and AI assistants can cite real reviews */}
      <script
        type="application/ld+json"
        data-schema="results-reviews"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(REVIEW_SCHEMA) }}
      />

      {/* HERO */}
      <V2Hero image={HERO.creatorLaptop} testId="results-hero">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Client Reviews</p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            What our podcast &amp; publishing clients say about working with Audio &amp; Co.
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            Real reviews from the coaches, consultants and authors we&rsquo;ve launched, produced and published. Podcast launches that hit top-10% worldwide, books ready for Amazon, and 200+ episode shows shipped on time, every time.
          </p>
          <div className="mt-8">
            <CTABlock size="lg" testIdPrefix="results-hero-cta" />
          </div>
        </div>
      </V2Hero>

      <FeaturedClientBuild testIdPrefix="results-featured-dimple" caseStudyHref="/case-studies/dimple-method" />

      {/* TESTIMONIALS */}
      <Section bg="white" testId="results-testimonials">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">In their words</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-3">
            Every quote below is from a real Audio &amp; Co. client.
          </h2>
          <p className="text-[#64748B] mt-4 max-w-2xl">
            Podcast hosts, coaches, consultants and authors we&rsquo;ve worked with across launch, production, publishing and audiobooks. No stock photos, no anonymised initials, no invented case studies.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7 flex flex-col"
              data-testid={`results-testimonial-${i}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Quote className="w-6 h-6 text-[#BC516F]" />
                <div className="flex" aria-label="5 out of 5 stars">
                  {[0,1,2,3,4].map(s => (
                    <Star key={s} className="w-4 h-4 text-[#BC516F] fill-[#BC516F]" />
                  ))}
                </div>
              </div>
              <blockquote className="text-[#1E2A44] leading-relaxed text-[16px] sm:text-[17px] flex-1">
                {t.quote}
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-[#E2E8F0]">
                <div className="font-display font-semibold text-[#1E2A44]">{t.name}</div>
                <div className="text-sm text-[#64748B]">{t.role}</div>
                <div className="text-xs uppercase tracking-widest text-[#BC516F] font-semibold mt-1.5">{t.service}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* BEFORE / AFTER, now clearly framed as Pro Plan system, not client-specific */}
      <Section bg="bg" testId="results-beforeafter">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">The Pro Plan snapshot</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-3">
            What the Pro Plan is built to deliver.
          </h2>
          <p className="text-[#64748B] mt-3 max-w-2xl">
            This is the weekly output the Pro Plan is engineered to produce. It is not a specific client result. It is the system: what you get when you plug in and stay consistent.
          </p>
        </div>
        <div className="mt-10 bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_10px_30px_rgba(30,42,68,0.04)]">
          <div className="hidden md:grid md:grid-cols-3 px-6 py-4 bg-[#F6F8FB] border-b border-[#E2E8F0]">
            <div className="font-display font-semibold text-[#1E2A44]">Metric</div>
            <div className="font-display font-semibold text-[#64748B]">Before the Pro Plan</div>
            <div className="font-display font-semibold text-[#BC516F]">Inside the Pro Plan</div>
          </div>
          {BEFORE_AFTER.map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-0 px-6 py-5 border-b border-[#E2E8F0] last:border-b-0">
              <div className="font-display font-semibold text-[#1E2A44]">{r.label}</div>
              <div className="text-[#64748B]"><span className="md:hidden text-xs uppercase tracking-wider mr-2">Before:</span>{r.before}</div>
              <div className="text-[#1E2A44]"><span className="md:hidden text-xs uppercase tracking-wider mr-2 text-[#BC516F]">Inside:</span>{r.after}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* MINI CASE STUDIES */}
      <Section bg="white" testId="results-cases">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Short stories</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-3">
            Six real client outcomes.
          </h2>
          <p className="text-[#64748B] mt-3 max-w-2xl">
            Podcasts, books, audiobooks, websites and full Pro Plan builds we&rsquo;ve shipped for real business owners. The scale ranges from a one-off book formatting to a complete multi-channel media operation.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CASE_STUDIES.map((c, i) => (
            <article key={i} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7 flex flex-col" data-testid={`results-case-${i}`}>
              <p className="text-xs uppercase tracking-widest text-[#BC516F] font-semibold">{c.business}</p>
              <h3 className="font-display font-semibold text-[#1E2A44] text-xl mt-2 leading-snug">{c.headline}</h3>
              <p className="text-[#475569] mt-3 leading-relaxed text-[15px]">{c.body}</p>
              {c.siteImage && (
                <a
                  href={c.siteImage.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 block overflow-hidden rounded-[10px] ring-1 ring-black/10 shadow-[0_14px_30px_-14px_rgba(30,42,68,0.45)] group"
                  data-testid={`results-case-${i}-site`}
                  aria-label={c.siteImage.alt}
                >
                  <img
                    src={c.siteImage.src}
                    alt={c.siteImage.alt}
                    title={`${c.business} | Audio & Co.`}
                    width="800"
                    height="450"
                    loading="lazy"
                    className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </a>
              )}
              {c.liveLink && (
                <a
                  href={c.liveLink.href}
                  {...(c.liveLink.internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                  className="mt-4 inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold text-sm"
                  data-testid={`results-case-${i}-live`}
                >
                  {c.liveLink.label} {c.liveLink.internal ? <ArrowRight className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                </a>
              )}
              {c.covers && c.covers.length > 0 && (
                <div className="mt-5 flex gap-3 items-end" data-testid={`results-case-${i}-covers`}>
                  {c.covers.map((cv, j) => (
                    <a
                      key={j}
                      href={cv.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block w-[86px] transform ${j % 2 === 0 ? '-rotate-2' : 'rotate-2'} hover:rotate-0 hover:scale-[1.04] transition-transform duration-500`}
                      aria-label={cv.alt}
                    >
                      <img
                        src={cv.src}
                        alt={cv.alt}
                        title="Published with Audio & Co."
                        width="172"
                        height="258"
                        loading="lazy"
                        className="w-full h-auto rounded-[4px] shadow-[0_16px_32px_-12px_rgba(30,42,68,0.55)] ring-1 ring-black/10"
                      />
                    </a>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </Section>

      {/* FEATURED CLIENT BUILD, BSF */}
      <Section bg="white" testId="results-featured-build">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <a
            href="https://www.britishsustainablefloristryacademy.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-[14px] overflow-hidden border border-[#E2E8F0] shadow-[0_18px_44px_rgba(30,42,68,0.12)] group"
            data-testid="results-bsf-image"
          >
            <img
              src="/portfolio/bsf.jpg?v=2"
              alt="The British Sustainable Floristry Academy homepage, a course platform and website built for SEO by Audio & Co."
              title="British Sustainable Floristry Academy | Audio & Co."
              loading="lazy"
              decoding="async"
              className="w-full aspect-[16/10] object-cover object-top transition duration-500 group-hover:scale-[1.02]"
            />
          </a>
          <div>
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Brand &amp; course platform build</p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-3 leading-tight">
              The British Sustainable Floristry Academy
            </h2>
            <p className="text-[#475569] mt-5 leading-relaxed text-[17px]">
              A brand and custom course platform, designed and built by Audio &amp; Co. from scratch. Editorial-grade visuals, thoughtful information architecture, and an infrastructure that supports both online learning and in-person retreats. British Flowers. Sustainable Techniques. Inspired Futures.
            </p>
            <ul className="mt-5 space-y-2 text-[#475569]">
              <li>Custom design and brand identity.</li>
              <li>Course delivery platform (Studio Online).</li>
              <li>Content architecture for retreats, journal, sustainability library.</li>
              <li>Bespoke copy and imagery treatment throughout.</li>
            </ul>
            <a
              href="https://www.britishsustainablefloristryacademy.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold"
              data-testid="results-bsf-visit"
            >
              Visit the live site <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </Section>

      {/* MORE BUILDS */}
      <Section bg="bg" testId="results-more-builds">
        <PortfolioGrid
          eyebrow="More recent builds"
          heading="What else Audio & Co. has shipped."
          subheading="A mix of client work and Jo's own businesses. Real sites, real revenue, all in production today."
        />
      </Section>

      {/* LATEST FROM THE BLOG */}
      <LatestFromBlog
        eyebrow="Read next"
        heading="How our clients get these results"
        subheading="The playbooks, positioning and SEO behind the wins on this page."
        bg="bg"
        testId="results-latest-blog"
      />

      {/* CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Could be your story by Q3.</h2>
          <p className="text-white/75 mt-4 text-lg">30-minute call. No pitch, no pressure.</p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="results-cta" />
          </div>
        </div>
      </Section>
    </main>
  );
}
